import React, { useState, useEffect } from "react";
import type { SinglePoints } from "../MyMaps";
import { storage } from "../FirebaseConfig";
import { getDownloadURL, ref } from "firebase/storage";


const SimpleCarousel = ({ points, initialIndex = 0 }: { points: SinglePoints[], initialIndex?: number }) => {
  const [index, setIndex] = useState(initialIndex);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const [isFallbackLoading, setIsFallbackLoading] = useState(false);
  
  // Update index when initialIndex changes, ensuring it's within bounds
  useEffect(() => {
    if (points.length > 0) {
      const validIndex = Math.max(0, Math.min(initialIndex, points.length - 1));
      setIndex(validIndex);
      setIsImageLoading(true);
    }
  }, [initialIndex, points.length]);
  
  // Ensure index is always valid and point exists
  const safeIndex = points.length > 0 ? Math.max(0, Math.min(index, points.length - 1)) : 0;
  const pt = points[safeIndex];

  // Fetch fallback image from Firebase Storage when no imageLink is present
  // This must be before any early returns (Rules of Hooks)
  useEffect(() => {
    let isCancelled = false;
    if (pt && !pt.imageLink) {
      setIsFallbackLoading(true);
      setFallbackUrl(null);
      const gsPath = "gs://cap-st-george-poi-map.appspot.com/Hotel/Cap-St-George-Hotel-small.jpg";
      const storageRef = ref(storage, gsPath);
      getDownloadURL(storageRef)
        .then((url) => {
          if (!isCancelled) setFallbackUrl(url);
        })
        .catch(() => {
          if (!isCancelled) setFallbackUrl(null);
        })
        .finally(() => {
          if (!isCancelled) setIsFallbackLoading(false);
        });
    } else {
      setFallbackUrl(null);
      setIsFallbackLoading(false);
    }
    return () => {
      isCancelled = true;
    };
  }, [pt]);

  const next = () => {
    setIsImageLoading(true);
    setIndex((prev) => (prev + 1) % points.length);
  };

  const prev = () => {
    setIsImageLoading(true);
    setIndex((prev) => (prev - 1 + points.length) % points.length);
  };

  if (!points || points.length === 0) {
    return <div className="w-full h-full p-4 text-center text-gray-500 flex items-center justify-center">No points available</div>;
  }

  // Safety check - if point doesn't exist, return early
  if (!pt) {
    return <div className="w-full h-full p-4 text-center text-gray-500 flex items-center justify-center">Point not found</div>;
  }

  return (
    <div className="w-full h-full flex flex-col">
      <h3 className="text-xl mb-2 flex-shrink-0">{pt.name}</h3>
      
      {/* Image container with fixed height */}
      <div className="flex-shrink-0 mb-2">
        {pt.imageLink ? (
          <div className="relative w-full h-40 rounded-md overflow-hidden bg-gray-200">
            {isImageLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            <img
              src={pt.imageLink}
              alt={pt.name}
              className={`w-full h-full object-cover transition-opacity duration-300 ${
                isImageLoading ? "opacity-0" : "opacity-100"
              }`}
              onLoad={() => setIsImageLoading(false)}
              onError={() => setIsImageLoading(false)}
            />
          </div>
        ) : fallbackUrl ? (
          <div className="relative w-full h-40 rounded-md overflow-hidden bg-gray-200">
            {isFallbackLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            <img
              src={fallbackUrl}
              alt=""
              className="w-full h-full object-cover opacity-60"
              onLoad={() => setIsFallbackLoading(false)}
              onError={() => setIsFallbackLoading(false)}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="px-2 py-1 rounded text-white text-sm font-medium bg-black/50">
                Image coming soon
              </span>
            </div>
          </div>
        ) : (
          <div className="relative w-full h-40 rounded-md overflow-hidden bg-gray-200">
            {isFallbackLoading ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="px-2 py-1 rounded text-white text-sm font-medium bg-black/50">
                  Image coming soon
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Description with fixed height and scroll */}
      <div className="flex-1 mb-3 overflow-y-auto">
        <p className="text-sm text-gray-700 font-normal">{pt.description}</p>
      </div>
      
      {/* Links */}
      <div className="flex flex-col gap-2 mb-3 flex-shrink-0">
        {pt.gmapsLink && (
          <a
            href={pt.gmapsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 text-sm hover:underline"
          >
            View on Google Maps
          </a>
        )}
        {pt.webLink && (
          <a
            href={pt.webLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 text-sm hover:underline"
          >
            Visit Website
          </a>
        )}
      </div>
      
              {/* Navigation */}
              <div className="flex justify-between items-center flex-shrink-0">
                <button
                  onClick={prev}
                  className="bg-transparent hover:bg-gray-200 px-3 py-1 rounded text-sm font-medium transition-colors"
                  disabled={points.length <= 1}
                >
                  ◀
                </button>
                <span className="text-sm text-gray-600">
                  {index + 1} / {points.length}
                </span>
                <button
                  onClick={next}
                  className="bg-transparent hover:bg-gray-200 px-3 py-1 rounded text-sm font-medium transition-colors"
                  disabled={points.length <= 1}
                >
                  ▶
                </button>
              </div>
    </div>
  );
};

export default SimpleCarousel;
